// Mesa calendar — re-skin of flatpickr to match the "Calendário · Mesa" design.
//
// Reuses flatpickr as the date engine (parsing, locale, navigation, day selection)
// and only overrides the look via the scoped `.mesa-cal` class added to the calendar
// container. The few non-visual additions are: single-letter weekdays, semantic
// highlight dots (pago/vencido) and a quick-pick footer.
//
// Modes:
//   inline  -> always-visible grid (no input), good for filter sidebars
//   popover -> a styled text field (calendar icon + input + chevron) that opens the grid.
//              With allowInput the field is typeable; otherwise it is read-only (click opens).
import { Controller } from "stimulus";
import flatpickr from "flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";

// Sunday-first single letters, matching the mock header (D S T Q Q S S).
const WEEKDAYS_ONE_LETTER = ["D", "S", "T", "Q", "Q", "S", "S"];

const QUICKS = [
  { label: "Hoje", days: 0 },
  { label: "Amanhã", days: 1 },
  { label: "+1 semana", days: 7 },
  { label: "+30 dias", days: 30 }
];

function pad(n) { return String(n).padStart(2, "0"); }

// Local-time ISO (avoids the UTC shift that Date#toISOString introduces).
function isoOf(date) { return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`; }

export default class extends Controller {
  static targets = ["input", "mount", "trigger", "readout"];
  static values = {
    mode: { type: String, default: "inline" },
    size: { type: String, default: "md" },
    today: String,
    min: String,
    max: String,
    showQuicks: { type: Boolean, default: true },
    highlights: { type: Array, default: [] },
    dateFormat: { type: String, default: "Y-m-d" },
    allowInput: { type: Boolean, default: false }
  };

  connect() {
    const inline = this.modeValue === "inline";

    this.fp = flatpickr(this.inputTarget, {
      inline,
      dateFormat: this.dateFormatValue,
      allowInput: this.allowInputValue,
      locale: this.buildLocale(),
      defaultDate: this.inputTarget.value || null,
      minDate: this.minValue || null,
      maxDate: this.maxValue || null,
      disableMobile: true,
      appendTo: inline ? (this.hasMountTarget ? this.mountTarget : this.element) : undefined,
      positionElement: this.hasTriggerTarget ? this.triggerTarget : undefined,
      onReady: (_dates, _str, fp) => this.handleReady(fp),
      onChange: (dates, _str, fp) => this.handleChange(dates, fp),
      onDayCreate: (_dObj, _dStr, _fp, dayElem) => this.decorateDay(dayElem),
      onOpen: () => this.hasTriggerTarget && this.triggerTarget.classList.add("is-open"),
      onClose: () => this.hasTriggerTarget && this.triggerTarget.classList.remove("is-open")
    });
  }

  disconnect() {
    this.fp && this.fp.destroy();
  }

  // --- flatpickr hooks ---

  handleReady(fp) {
    fp.calendarContainer.classList.add("mesa-cal", `mesa-cal--${this.sizeValue}`);
    if (this.showQuicksValue) this.buildFooter(fp);
  }

  handleChange(dates, fp) {
    this.updateReadout(dates[0] ? isoOf(dates[0]) : "");
    if (this.modeValue === "popover" && dates[0]) fp.close();
  }

  decorateDay(dayElem) {
    if (!dayElem.dateObj) return;

    const dow = dayElem.dateObj.getDay();
    if (dow === 0 || dow === 6) dayElem.classList.add("is-weekend");

    const hl = this.highlightsValue.find((h) => h.iso === isoOf(dayElem.dateObj));
    if (!hl) return;

    dayElem.classList.add(`is-hl-${hl.kind}`);
    if (hl.label) dayElem.title = hl.label;

    const dot = document.createElement("span");
    dot.className = "mesa-cal__hl";
    if (hl.color) dot.style.background = hl.color;
    dayElem.appendChild(dot);
  }

  // --- quick-pick footer (not native to flatpickr) ---

  buildFooter(fp) {
    const foot = document.createElement("footer");
    foot.className = "mesa-cal__foot";

    const quicks = document.createElement("div");
    quicks.className = "mesa-cal__quicks";
    QUICKS.forEach(({ label, days }) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "mesa-cal__quick";
      btn.textContent = label;
      btn.addEventListener("click", () => fp.setDate(this.baseDatePlus(days), true));
      quicks.appendChild(btn);
    });

    const clear = document.createElement("button");
    clear.type = "button";
    clear.className = "mesa-cal__clear";
    clear.textContent = "Limpar";
    clear.addEventListener("click", () => fp.clear(true));

    foot.append(quicks, clear);
    fp.calendarContainer.appendChild(foot);
  }

  baseDatePlus(days) {
    const base = this.todayValue ? new Date(`${this.todayValue}T00:00:00`) : new Date();
    base.setHours(0, 0, 0, 0);
    base.setDate(base.getDate() + days);
    return base;
  }

  // --- field interaction + demo readout ---

  // Opens the calendar when the field chrome (icon/chevron) is clicked.
  open() {
    this.fp && this.fp.open();
  }

  updateReadout(iso) {
    if (this.hasReadoutTarget) this.readoutTarget.textContent = iso || "—";
  }

  buildLocale() {
    return {
      ...Portuguese,
      firstDayOfWeek: 0,
      weekdays: { ...Portuguese.weekdays, shorthand: WEEKDAYS_ONE_LETTER }
    };
  }
}
